/**
 * Language detection.
 *
 * Only two languages are in play, and they use different alphabets, so this needs no model and
 * no library — counting letters is both faster and more reliable than any heuristic over words.
 *
 * The one genuinely hard case is transliterated Russian ("privet, kak dela"), which is Latin
 * script but not English. A short stop-word list catches the common ones; anything it misses
 * falls through to English, which is the safer wrong answer because the live joke APIs are
 * English and the user can always flip the switch.
 */

import type { Lang } from './types.js';

/** Cyrillic block, including Ё and ё which sit outside the main range. */
const CYRILLIC = /[Ѐ-ӿ]/g;
const LATIN = /[A-Za-z]/g;

/**
 * Transliterated Russian that is unambiguous — no English word is spelled this way. Kept
 * deliberately short: a long list starts producing false positives on English text.
 */
const TRANSLIT_MARKERS = [
  'privet',
  'poka',
  'spasibo',
  'pozhaluysta',
  'kak dela',
  'chto',
  'pochemu',
  'khorosho',
  'ploho',
  'davay',
  'nichego',
  'rabota',
  'druzhba',
];

export interface Detection {
  lang: Lang;
  /** 0 to 1. Low confidence means the UI should keep the manual switch prominent. */
  confidence: number;
  /** Why the decision was made, for the diagnostics panel. */
  reason: 'cyrillic' | 'latin' | 'transliteration' | 'empty';
}

/**
 * Detects the language of a phrase.
 *
 * @param text raw user input, as spoken or typed
 * @param fallback what to return when there is nothing to go on
 */
export function detectLanguage(text: string, fallback: Lang = 'en'): Detection {
  const trimmed = text.trim();
  if (trimmed.length === 0) return { lang: fallback, confidence: 0, reason: 'empty' };

  const cyrillic = (trimmed.match(CYRILLIC) ?? []).length;
  const latin = (trimmed.match(LATIN) ?? []).length;
  const letters = cyrillic + latin;

  if (letters === 0) return { lang: fallback, confidence: 0, reason: 'empty' };

  if (cyrillic > 0) {
    const share = cyrillic / letters;
    // Any Cyrillic at all is decisive: English text does not contain it by accident.
    return { lang: 'ru', confidence: Math.max(0.6, share), reason: 'cyrillic' };
  }

  const lower = trimmed.toLowerCase();
  if (TRANSLIT_MARKERS.some((marker) => lower.includes(marker))) {
    return { lang: 'ru', confidence: 0.5, reason: 'transliteration' };
  }

  return { lang: 'en', confidence: latin / letters, reason: 'latin' };
}

/** BCP-47 tags for the Web Speech API, which needs the language set before it starts. */
export const SPEECH_LOCALE: Record<Lang, string> = {
  ru: 'ru-RU',
  en: 'en-US',
};

export const LANG_LABEL: Record<Lang, string> = {
  ru: 'Русский',
  en: 'English',
};
